import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { isValidSignupPhone } from '@/hooks/useAuth';
import {
  useGetOfficeDashboardQuery,
  type OfficeDashboardAppLimit,
} from '@/redux/api/offices.rtkq';
import { useUpdateMyPhoneMutation } from '@/redux/api/users.rtkq';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  ExpiredSubscriptionChip,
  RenewSubscriptionDialog,
  isOfficeSubscriptionBlocked,
} from '@/components/subscription/RenewSubscriptionDialog';

const formatNumber = (num: number) => num.toLocaleString('he-IL');

const usagePercent = (used: number, total: number) =>
  total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

const remainingPercent = (remaining: number, limit: number) =>
  limit > 0 ? Math.min(100, Math.round((remaining / limit) * 100)) : 0;

const formatQuota = (used: number, limit: number | null) =>
  limit == null ? `${formatNumber(used)} / ללא הגבלה` : `${formatNumber(used)} / ${formatNumber(limit)}`;

const CreditStat = ({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div
      className={
        emphasize
          ? 'text-lg font-semibold text-foreground tabular-nums'
          : 'text-sm text-foreground tabular-nums'
      }
    >
      {value}
    </div>
  </div>
);

const OfficeDashboardPage = () => {
  const { getAccountId, isAccountAdmin, userProfile } = useAuthContext();
  const officeId = getAccountId() || '';
  const { data, isLoading } = useGetOfficeDashboardQuery(officeId, {
    skip: !officeId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const [contactsOpen, setContactsOpen] = useState(false);
  const openedContactsOnce = useRef(false);
  const canManageOffice = isAccountAdmin();
  const isMemberView = data?.view === 'member' || (!canManageOffice && !!data?.me);
  const isBlocked = isOfficeSubscriptionBlocked(data?.office.subscriptionStatus);

  useEffect(() => {
    if (isMemberView || !isBlocked || openedContactsOnce.current) return;
    openedContactsOnce.current = true;
    setContactsOpen(true);
  }, [isBlocked, isMemberView]);

  if (isLoading)
    return <div className="p-6 text-muted-foreground">טוען...</div>;
  if (!data)
    return <div className="p-6 text-muted-foreground">אין נתונים זמינים</div>;

  if (isMemberView) {
    const email = data.me?.email || userProfile?.email || '—';
    const userPhone =
      data.me?.phoneNumber || userProfile?.phoneNumber || '';
    const officePhone = data.office.phoneNumber || '';
    return (
      <div className="container mx-auto max-w-2xl p-4 md:p-6" dir="rtl">
        <h1 className="mb-6 text-2xl font-bold text-foreground">המשרד שלי</h1>
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div>
            <div className="text-sm text-muted-foreground">אימייל</div>
            <div className="text-lg font-medium text-foreground break-all">{email}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">שם המשרד</div>
            <div className="text-lg font-medium text-foreground break-words">
              {data.office.name || '—'}
            </div>
          </div>
          <EditablePhone
            label="טלפון משתמש"
            value={userPhone}
            emphasize="lg"
          />
          <div>
            <div className="text-sm text-muted-foreground">טלפון משרד</div>
            <div className="text-lg font-medium text-foreground" dir="ltr">
              {officePhone || '—'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const monthlyAllocation = data.office.monthlyCreditAllocation || 0;
  const remainingBalance = data.office.creditBalance || 0;
  const bonusTotal = Object.values(data.office.appBonusCreditBalances || {}).reduce(
    (sum, amount) => sum + (Number.isFinite(amount) ? amount : 0),
    0,
  );
  const appLimits = data.usage?.apps || [];
  const periodEnd = data.office.currentPeriodEnd
    ? new Date(data.office.currentPeriodEnd)
    : null;
  const daysUntilFree =
    periodEnd && Number.isFinite(periodEnd.getTime())
      ? Math.ceil((periodEnd.getTime() - Date.now()) / 86_400_000)
      : null;
  const statusLabel =
    data.office.subscriptionStatus === 'expired'
      ? 'חסום'
      : data.office.subscriptionStatus === 'cancelled'
        ? 'מבוטל'
        : data.office.subscriptionStatus === 'active'
          ? 'פעיל'
          : data.office.subscriptionStatus;

  return (
    <div className="container mx-auto max-w-5xl p-4 md:p-6" dir="rtl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-foreground">לוח בקרה</h1>
        <div className="flex flex-wrap items-center gap-2">
          {!isBlocked && daysUntilFree != null && daysUntilFree > 0 && (
            <Badge variant="outline">
              המנוי ישודרג ל-Free בעוד {daysUntilFree} ימים
            </Badge>
          )}
          {isBlocked && (
            <ExpiredSubscriptionChip onOpenContacts={() => setContactsOpen(true)} />
          )}
        </div>
      </div>

      <RenewSubscriptionDialog open={contactsOpen} onOpenChange={setContactsOpen} />

      {/* Stats cards */}
      <div
        inert={isBlocked || undefined}
        className={
          isBlocked
            ? 'pointer-events-none select-none opacity-50'
            : undefined
        }
      >
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">שם המשרד</div>
          <div className="text-xl font-bold text-foreground break-words">
            {data.office.name || '—'}
          </div>
        </div>
        <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-4">
          <EditablePhone
            label="טלפון משתמש"
            value={
              data.me?.phoneNumber || userProfile?.phoneNumber || ''
            }
            emphasize="xl"
          />
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">טלפון משרד</div>
          <div
            className="mt-1 text-xl font-bold break-words text-foreground"
            dir="ltr"
          >
            {data.office.phoneNumber || '—'}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">תוכנית</div>
          <div className="text-xl font-bold text-foreground break-words">
            {data.office.planName || '—'}
          </div>
          <div
            className={
              isBlocked
                ? 'text-xs font-medium text-destructive'
                : 'text-xs text-muted-foreground'
            }
          >
            {statusLabel}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">חברי צוות</div>
          <div className="text-2xl font-bold text-foreground">
            {data.members?.total ?? 0}
          </div>
          <div className="text-xs text-muted-foreground">
            {data.members?.admins ?? 0} מנהלים, {data.members?.users ?? 0} משתמשים
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">תיקים</div>
          <div className="text-2xl font-bold text-foreground">
            {data.cases?.total ?? 0}
          </div>
          <div className="text-xs text-muted-foreground">
            {data.cases?.active ?? 0} פעילים, {data.cases?.archived ?? 0} בארכיון
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 md:col-span-2">
          <div className="text-sm text-muted-foreground mb-2">קרדיטים</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CreditStat
              label="יתרת חלון"
              value={formatNumber(remainingBalance)}
              emphasize
            />
            <CreditStat
              label="בונוס"
              value={formatNumber(bonusTotal)}
              emphasize
            />
            <CreditStat
              label="נוצלו"
              value={formatNumber(data.usage?.totalCreditsConsumed ?? 0)}
            />
            <CreditStat
              label="הקצאה חודשית"
              value={
                monthlyAllocation > 0
                  ? formatNumber(monthlyAllocation)
                  : 'ללא הגבלה'
              }
            />
          </div>
          {data.office.quotaWindowEnd && (
            <div className="text-xs text-muted-foreground mt-3">
              איפוס חלון: {new Date(data.office.quotaWindowEnd).toLocaleString('he-IL')}
            </div>
          )}
        </div>
      </div>

      {/* Per-app limits */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-1">מגבלות לפי אפליקציה</h2>
        <p className="text-xs text-muted-foreground mb-4">
          הקצאת חלון, בונוס, ומכסה חודשית לפי התוכנית
        </p>
        {appLimits.length > 0 ? (
          <div className="space-y-4">
            {appLimits.map((app) => (
              <AppLimitRow key={app.appIdentifier} app={app} />
            ))}
          </div>
        ) : Object.keys(data.usage?.creditsByApp || {}).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(data.usage?.creditsByApp || {}).map(([app, credits]) => (
              <div key={app} className="flex justify-between items-center">
                <span className="text-foreground">{app}</span>
                <span className="text-muted-foreground font-mono">
                  {formatNumber(credits)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">אין אפליקציות בתוכנית</p>
        )}
      </div>

      {/* Recent cases */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">תיקים שעודכנו לאחרונה</h2>
        {!data.cases?.recentlyUpdated?.length ? (
          <p className="text-muted-foreground">אין תיקים עדיין</p>
        ) : (
          <div className="space-y-2">
            {data.cases.recentlyUpdated.map((c: any) => (
              <div
                key={c.caseId}
                className="flex justify-between items-center border-b border-border pb-2 last:border-0"
              >
                <div>
                  <span className="font-medium text-foreground">{c.title}</span>
                  <span className="text-sm text-muted-foreground mr-2">
                    ({c.caseId})
                  </span>
                </div>
                <span className="text-xs text-muted-foreground capitalize">
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members list — visible to office admin and owner */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3">צוות</h2>
        <div className="space-y-2">
          {(data.members?.list || []).map((m) => (
            <div key={m.userId} className="flex justify-between items-center gap-3">
              <div className="min-w-0">
                <span className="text-foreground">
                  {m.displayName || m.email}
                </span>
                {m.isOwner && (
                  <span className="me-2 rounded-full bg-chen-900 px-2 py-0.5 text-xs text-white">
                    בעלים
                  </span>
                )}
                <span className="text-xs text-muted-foreground mr-2">
                  {m.role === 'office_admin' ? 'מנהל' : 'משתמש'}
                </span>
              </div>
              <span className="text-xs text-muted-foreground truncate">{m.email}</span>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

const EditablePhone = ({
  value,
  label = 'טלפון משתמש',
  emphasize = 'lg',
}: {
  value: string;
  label?: string;
  emphasize?: 'lg' | 'xl';
}) => {
  const { refreshUser } = useAuthContext();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [updatePhone, { isLoading }] = useUpdateMyPhoneMutation();

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const valueClass =
    emphasize === 'xl'
      ? 'text-xl font-bold text-foreground break-words'
      : 'text-lg font-medium text-foreground break-words';

  const startEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value);
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    const phone = draft.trim();
    if (!phone) {
      setError('נא להזין מספר טלפון');
      return;
    }
    if (!isValidSignupPhone(phone)) {
      setError('מספר טלפון לא תקין');
      return;
    }
    try {
      await updatePhone({ phoneNumber: phone }).unwrap();
      await refreshUser();
      setEditing(false);
      setError(null);
    } catch (err: any) {
      const msg = err?.data?.message;
      setError(
        typeof msg === 'string'
          ? msg
          : msg?.message || 'שגיאה בשמירת הטלפון',
      );
    }
  };

  if (editing) {
    return (
      <div className="min-w-0 space-y-2">
        <div className="text-sm text-muted-foreground">{label}</div>
        <input
          type="text"
          inputMode="tel"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-start text-foreground"
          autoFocus
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={save}
            disabled={isLoading}
            className="rounded-lg bg-chen-900 px-3 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
          >
            {isLoading ? '...' : 'שמור'}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={isLoading}
            className="rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/50 disabled:opacity-50"
          >
            ביטול
          </button>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 flex flex-wrap items-center justify-start gap-2">
        <div className={valueClass}>
          {value || '—'}
        </div>
        <button
          type="button"
          onClick={startEdit}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          aria-label="ערוך טלפון משתמש"
        >
          <Pencil className="h-3.5 w-3.5" />
          עריכה
        </button>
      </div>
    </div>
  );
};

const AppLimitRow = ({ app }: { app: OfficeDashboardAppLimit }) => {
  // Plan includedCredits=0 still can have a wallet balance (admin set
  // app_credit_balances) or bonus — show those, not "no dedicated allocation".
  const hasDedicatedPool =
    app.includedCredits > 0 ||
    app.remainingCredits != null ||
    (app.bonusCredits ?? 0) > 0;
  const windowRemaining = app.remainingCredits;
  const planWindowNorm = app.usageLimits.window ?? null;
  // When the plan grants 0 but an admin loaded the window wallet, use remaining
  // as the visible "norm" so the progress bar is meaningful.
  const windowLimit =
    planWindowNorm && planWindowNorm > 0
      ? planWindowNorm
      : windowRemaining != null && windowRemaining > 0
        ? windowRemaining
        : planWindowNorm;
  // Keep monthly 0 as 0 (not "unlimited") when the app is on the plan.
  const monthlyLimit =
    app.usageLimits.monthly ??
    (app.includedCredits > 0 ? app.includedCredits : 0);
  const monthlyUsed = app.usedThisMonth.credits;
  const bonus = app.bonusCredits ?? 0;

  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-medium text-foreground">{app.name}</div>
        <Badge variant="outline" className="font-mono text-[11px]">
          {app.appIdentifier}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-3">
          <div className="text-xs text-muted-foreground mb-2">חלון קצובה</div>
          {hasDedicatedPool ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <CreditStat
                  label="נותרו בחלון"
                  value={formatNumber(windowRemaining ?? 0)}
                  emphasize
                />
                <CreditStat
                  label="נורמת חלון"
                  value={formatNumber(windowLimit ?? 0)}
                />
                <CreditStat
                  label="בונוס"
                  value={formatNumber(bonus)}
                />
              </div>
              <Progress
                value={remainingPercent(windowRemaining ?? 0, windowLimit ?? 0)}
                className="h-2"
              />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="text-sm text-muted-foreground">אין הקצאה ייעודית</div>
              <CreditStat
                label="בונוס"
                value={formatNumber(bonus)}
              />
            </div>
          )}
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">מכסה חודשית</div>
          <div className="text-sm text-foreground">
            {formatQuota(monthlyUsed, monthlyLimit)}
          </div>
          {monthlyLimit != null && monthlyLimit > 0 && (
            <Progress value={usagePercent(monthlyUsed, monthlyLimit)} className="h-2 mt-1" />
          )}
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">עלות פעולה</div>
          <div className="text-sm text-foreground">
            {formatNumber(app.creditCost)} קרדיטים
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficeDashboardPage;
